import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useConnectionStore } from '@/stores/connectionStore'
import { useForm, Controller } from 'react-hook-form'
import { XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { useAgents } from '@/hooks/useAgents'
import { useCreateDataSourceMutation, useUpdateDataSourceMutation } from '@/hooks/useDataSources'
import { PaginatedSelect } from '@/components/common/PaginatedSelect'
import type { Agent } from '@/services/api/models/Agent'
import type { DataSourceDetails } from '@/services/api/models/DataSourceDetails'
import { toast } from 'react-hot-toast'

interface ConnectionModalProps {
  existingConnection?: DataSourceDetails
  onClose: () => void
}

interface ConnectionFormData {
  name: string
  type: 'mysql' | 'postgresql'
  host: string
  port: number
  username: string
  password: string
  database?: string
  ssl?: boolean
  agentId: string // Required for API
  isLocked?: boolean
}


export function ConnectionModal({ existingConnection, onClose }: ConnectionModalProps) {
  const { addConnection, updateConnection } = useConnectionStore()
  const [showPassword, setShowPassword] = useState(false)
  
  // API mutations for datasource operations
  const createDataSourceMutation = useCreateDataSourceMutation()
  const updateDataSourceMutation = useUpdateDataSourceMutation()
  
  // Pagination state for agents
  const [agentPage, setAgentPage] = useState(1)
  const [hasMoreAgents, setHasMoreAgents] = useState(true)
  const [allAgents, setAllAgents] = useState<Agent[]>([])
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [isLoadingMoreAgents, setIsLoadingMoreAgents] = useState(false)
  
  // Fetch agents for the dropdown
  const { agents, loading: agentsLoading, error: agentsError, fetchAgents, getAgent, pagination } = useAgents()

  // Calculate form default values based on mode
  const defaultValues = useMemo((): ConnectionFormData => {
    return existingConnection ? {
      name: existingConnection.displayName || '',
      type: (existingConnection.databaseType === 'MySQL' ? 'mysql' : 'postgresql'),
      host: existingConnection.host || 'localhost',
      port: existingConnection.port || (existingConnection.databaseType === 'MySQL' ? 3306 : 5432),
      username: existingConnection.username || '',
      password: '', // Never populate password for security
      database: existingConnection.defaultDatabase || '',
      ssl: (existingConnection as any).connectionParams?.ssl || false,
      agentId: existingConnection.agentId || '',
      isLocked: existingConnection.isLocked || false, // Load lock status from server
    } : {
      name: '',
      type: 'mysql',
      host: 'localhost',
      port: 3306,
      username: '',
      password: '',
      database: '',
      ssl: false,
      agentId: '',
      isLocked: false,
    }
  }, [existingConnection])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<ConnectionFormData>({
    defaultValues
  })

  const watchType = watch('type')

  // Only set default port for NEW connections when type changes
  useEffect(() => {
    if (!existingConnection) {
      if (watchType === 'mysql') {
        setValue('port', 3306)
      } else {
        setValue('port', 5432)
      }
    }
  }, [watchType, setValue, existingConnection])

  // Track if we've fetched agents to prevent duplicates
  const hasFetchedAgentsRef = useRef(false)
  
  // Load initial agents only when modal opens and agents haven't been loaded
  useEffect(() => {
    // Only fetch if we haven't fetched yet and there are no agents
    if (!hasFetchedAgentsRef.current && agents.length === 0 && !agentsLoading) {
      hasFetchedAgentsRef.current = true
      const loadInitialAgents = async () => {
        await fetchAgents(1, 20)
      }
      void loadInitialAgents()
    }
  }, [fetchAgents, agents.length, agentsLoading])

  // Update all agents when new page is loaded
  useEffect(() => {
    if (agents.length > 0) {
      setAllAgents(prev => {
        // Create a map to ensure uniqueness by ID
        const agentMap = new Map(prev.map(agent => [agent.id, agent]))
        for (const agent of agents) agentMap.set(agent.id, agent)
        return Array.from(agentMap.values())
      })
      
      // Update hasMoreAgents based on pagination info
      if (pagination) {
        setHasMoreAgents(pagination.has_next ?? false)
      }
    }
  }, [agents, pagination])

  // Load specific agent data when editing
  useEffect(() => {
    const loadSelectedAgent = async () => {
      if (existingConnection?.agentId && !selectedAgent) {
        // First check if agent is already in our loaded list
        const foundAgent = allAgents.find(a => a.id === existingConnection.agentId)
        if (foundAgent) {
          setSelectedAgent(foundAgent)
        } else {
          // If not found, fetch the specific agent
          const agent = await getAgent(existingConnection.agentId)
          if (agent) {
            setSelectedAgent(agent)
            // Add to allAgents if not already there
            setAllAgents(prev => {
              const exists = prev.some(a => a.id === agent.id)
              return exists ? prev : [...prev, agent]
            })
          }
        }
      }
    }
    void loadSelectedAgent()
  }, [existingConnection, allAgents, getAgent, selectedAgent])

  // Reset form when defaultValues change (e.g., when switching between connections)
  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  // Callback functions for PaginatedSelect to avoid creating new functions in JSX
  const handleLoadMoreAgents = useCallback(() => {
    if (isLoadingMoreAgents || !hasMoreAgents) return
    
    const loadMore = async () => {
      setIsLoadingMoreAgents(true)
      try {
        await fetchAgents(agentPage + 1, 20)
        setAgentPage(prev => prev + 1)
      } catch (error: unknown) {
        console.error('Failed to load more agents:', error)
      } finally {
        setIsLoadingMoreAgents(false)
      }
    }
    void loadMore()
  }, [isLoadingMoreAgents, hasMoreAgents, fetchAgents, agentPage])

  const getAgentValue = useCallback((agent: Agent) => agent.id || '', [])

  const getAgentLabel = useCallback((agent: Agent) => {
    const statusIcon = 
      agent.connection_status === 'connected' ? '🟢' : 
      agent.connection_status === 'error' ? '🔴' : 
      '🟡'
    const statusText = 
      agent.connection_status === 'connected' ? 'Connected' : 
      agent.connection_status === 'error' ? 'Error' : 
      'Disconnected'
    return `${agent.name} ${statusIcon} (${statusText})`
  }, [])

  // Memoized filtered agents to avoid creating new objects in JSX
  const filteredAgents = useMemo(() => 
    allAgents.filter(agent => agent.is_active !== false), 
    [allAgents]
  )

  // Memoized validation rules
  const agentValidationRules = useMemo(() => ({ 
    required: 'Agent selection is required' 
  }), [])

  // Render function for Controller to avoid creating new functions in JSX
  const renderAgentSelect = useCallback(({ field }: { field: { value: string; onChange: (value: string) => void } }) => (
    <PaginatedSelect
      value={field.value}
      onChange={field.onChange}
      placeholder="Select an agent (required)"
      isLoading={agentsLoading}
      isFetchingNextPage={isLoadingMoreAgents}
      hasNextPage={hasMoreAgents}
      onFetchNextPage={handleLoadMoreAgents}
      options={filteredAgents}
      getOptionValue={getAgentValue}
      getOptionLabel={getAgentLabel}
      disabled={agentsLoading && allAgents.length === 0}
      className="w-full"
      dropdownDirection="auto"
      maxDropdownHeight="10rem"
    />
  ), [agentsLoading, isLoadingMoreAgents, hasMoreAgents, handleLoadMoreAgents, filteredAgents, getAgentValue, getAgentLabel, allAgents.length])

  const onSubmit = async (data: ConnectionFormData) => {
    try {
      // Validate that an agent is selected
      if (!data.agentId) {
        toast.error('Please select an agent. All datasources must be associated with an agent.')
        return
      }

      if (existingConnection && existingConnection.id) {
        // Update existing datasource via API
        const updateData: {
          displayName: string
          databaseType: 'MySQL' | 'PostgreSQL'
          host: string
          port: number
          username: string
          password?: string
          defaultDatabase: string | null
          connectionParams: { ssl: boolean } | null
          agentId: string
          isLocked: boolean
        } = {
          displayName: data.name,
          databaseType: data.type === 'mysql' ? 'MySQL' as const : 'PostgreSQL' as const,
          host: data.host,
          port: data.port,
          username: data.username,
          defaultDatabase: data.database || null,
          connectionParams: data.ssl ? { ssl: true } : null,
          agentId: data.agentId,
          isLocked: data.isLocked || false
        }
        
        // Only include password if it's provided (not empty)
        if (data.password && data.password.trim()) {
          updateData.password = data.password
        }
        
        await updateDataSourceMutation.mutateAsync({
          id: existingConnection.id,
          data: updateData
        })
        
        // Also update local store for UI consistency
        updateConnection(existingConnection.id, data)
        toast.success('Connection updated successfully')
      } else {
        // Create new datasource via API
        const newDataSource = await createDataSourceMutation.mutateAsync({
          displayName: data.name,
          databaseType: data.type === 'mysql' ? 'MySQL' : 'PostgreSQL',
          host: data.host,
          port: data.port,
          username: data.username,
          password: data.password,
          defaultDatabase: data.database || null,
          connectionParams: data.ssl ? { ssl: true } : null,
          agentId: data.agentId,
          isLocked: data.isLocked || false
        })
        
        // Add to local store for UI consistency (convert API response to local format)
        const localConnection = {
          ...data,
          id: newDataSource.id || crypto.randomUUID(),
          createdAt: newDataSource.createdAt || new Date().toISOString(),
          updatedAt: newDataSource.updatedAt || new Date().toISOString(),
        }
        addConnection(localConnection)
        toast.success('Connection created successfully')
      }
      
      onClose()
    } catch (error) {
      console.error('Failed to save connection:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to save connection'
      toast.error(errorMessage)
    }
  }


  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {existingConnection ? 'Edit Database Connection' : 'New Database Connection'}
              </h3>
              <button type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Connection Name
                </label>
                <input
                  {...register('name', { required: 'Connection name is required' })}
                  className="input w-full"
                  placeholder="My Database"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Database Type
                </label>
                <select
                  {...register('type', { required: 'Database type is required' })}
                  className="input w-full"
                >
                  <option value="mysql">MySQL</option>
                  <option value="postgresql">PostgreSQL</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Host
                  </label>
                  <input
                    {...register('host', { required: 'Host is required' })}
                    className="input w-full"
                    placeholder="localhost"
                  />
                  {errors.host && (
                    <p className="mt-1 text-sm text-red-600">{errors.host.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Port
                  </label>
                  <input
                    {...register('port', { 
                      required: 'Port is required',
                      valueAsNumber: true,
                      min: { value: 1, message: 'Port must be greater than 0' },
                      max: { value: 65535, message: 'Port must be less than 65536' }
                    })}
                    type="number"
                    className="input w-full"
                  />
                  {errors.port && (
                    <p className="mt-1 text-sm text-red-600">{errors.port.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Username
                </label>
                <input
                  {...register('username', { required: 'Username is required' })}
                  className="input w-full"
                  placeholder="username"
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    {...register('password', { 
                      required: existingConnection ? false : 'Password is required' 
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className="input w-full pr-10"
                    placeholder={existingConnection ? "Enter password (leave blank to keep current)" : "password"}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => { setShowPassword(!showPassword) }}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Database (Optional)
                </label>
                <input
                  {...register('database')}
                  className="input w-full"
                  placeholder="database_name"
                />
              </div>

              <div className="flex items-center">
                <input
                  {...register('ssl')}
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Use SSL
                </label>
              </div>

              <div className="border-t pt-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    {...register('isLocked')}
                    type="checkbox"
                    className="mt-1 h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <div className="flex-1">
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Lock Connection (Production Safety)
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Require verification code before connecting. Use this for production databases to prevent accidental connections.
                    </span>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Agent <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="agentId"
                  control={control}
                  rules={agentValidationRules}
                  render={renderAgentSelect}
                />
                {errors.agentId && (
                  <p className="mt-1 text-sm text-red-600">{errors.agentId.message}</p>
                )}
                {agentsError && (
                  <p className="mt-1 text-sm text-red-600">Failed to load agents</p>
                )}
                {!agentsLoading && !agentsError && allAgents.length === 0 && (
                  <div className="mt-1 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                    <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                      No agents available. You need to create an agent first.
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      All datasources must be associated with an agent to handle database operations.
                    </p>
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  All datasources must be associated with an agent to handle database operations
                </p>
              </div>
            </form>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button type="button"
              onClick={() => void handleSubmit(onSubmit)()}
              disabled={createDataSourceMutation.isPending || updateDataSourceMutation.isPending}
              className="btn-primary btn-md w-full sm:ml-3 sm:w-auto"
            >
              {createDataSourceMutation.isPending || updateDataSourceMutation.isPending 
                ? (existingConnection ? 'Updating...' : 'Creating...') 
                : (existingConnection ? 'Save Changes' : 'Create Connection')
              }
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={createDataSourceMutation.isPending || updateDataSourceMutation.isPending}
              className="btn-ghost btn-md mt-3 w-full sm:mt-0 sm:ml-3 sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}